import {Component, OnInit, OnDestroy, inject, Input, signal, computed} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatMenuModule} from '@angular/material/menu';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {Subject, debounceTime, takeUntil} from 'rxjs';

import {ISupportRequest} from '../../../interfaces/player/ISupportRequest';
import {ISupportRequestFilter} from '../../../interfaces/ISupportRequestFilter';
import {SupportRequestService} from '../../../services/support-request.service';
import {ActiveUserService} from '../../../services/active-user.service';
import {GameService} from '../../../services/game.service';
import {SnackbarService} from '../../../services/snackbar.service';
import {PermissionsService} from '../../../services/permissions.service';
import {SupportDeskColDefs} from '../support-desk-column-defs';
import {PermissionTypes} from '../../../enum/PermissionTypes';
import {IAssignAdminToSupportRequest} from '../../../interfaces/player/IAssignAdminToSupportRequest';
import {ContainerComponent} from '@coreui/angular';

import {KanbanColumnComponent} from './components/kanban-column/kanban-column.component';
import {TicketDetailPanelComponent} from './components/ticket-detail-panel/ticket-detail-panel.component';

@Component({
  selector: 'app-support-desk-kanban',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    ContainerComponent,
    KanbanColumnComponent,
    TicketDetailPanelComponent
  ],
  templateUrl: './support-desk-kanban.component.html',
  styleUrl: './support-desk-kanban.component.scss'
})
export class SupportDeskKanbanComponent implements OnInit, OnDestroy {
  private supportRequestService = inject(SupportRequestService);
  private activeUserService = inject(ActiveUserService);
  private gameService = inject(GameService);
  private snackBarService = inject(SnackbarService);
  private permissionsService = inject(PermissionsService);
  private supportDeskColDefs = inject(SupportDeskColDefs);

  @Input() isCrossGameView = false;

  // Tickets organized by column (using signals)
  unassignedTickets = signal<ISupportRequest[]>([]);
  assignedTickets = signal<ISupportRequest[]>([]);
  myWorkTickets = signal<ISupportRequest[]>([]);
  escalatedTickets = signal<ISupportRequest[]>([]);
  resolvedTickets = signal<ISupportRequest[]>([]);

  // Computed signal for total open tickets
  totalOpenTickets = computed(() =>
    this.unassignedTickets().length +
    this.assignedTickets().length +
    this.myWorkTickets().length +
    this.escalatedTickets().length
  );

  // UI State
  isLoading = true;
  selectedTicket: ISupportRequest | null = null;
  isPanelOpen = false;
  isVerticalView = signal(false);

  // Column visibility
  columnVisibility = signal({
    unassigned: true,
    assigned: true,
    myWork: true,
    escalated: true,
    resolved: true
  });

  // Search and filter
  searchControl = new FormControl('');
  errorTypeControl = new FormControl<number | null>(null);
  errorTypes = this.supportDeskColDefs.errorTypes;

  // Permissions
  canViewEscalated = false;

  private destroy$ = new Subject<void>();
  private allTickets: ISupportRequest[] = [];

  ngOnInit(): void {
    this.canViewEscalated = this.permissionsService.userHasPermission(PermissionTypes.HandleEscalatedSupportRequests);
    this.loadTickets();
    this.setupSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.filterAndOrganizeTickets();
    });

    this.errorTypeControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.filterAndOrganizeTickets();
    });
  }

  loadTickets(): void {
    this.isLoading = true;

    const filter: ISupportRequestFilter = {
      GameId: this.isCrossGameView ? '' : this.gameService.activeGame().Id,
      PageNumber: 1,
      PageSize: 500 // Get all tickets for Kanban view
    };

    this.supportRequestService.getSupportRequests(filter).subscribe({
      next: (tickets) => {
        this.allTickets = tickets;
        this.filterAndOrganizeTickets();
        this.isLoading = false;
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to load support tickets');
        this.isLoading = false;
      }
    });
  }

  private filterAndOrganizeTickets(): void {
    let filtered = [...this.allTickets];

    // Apply search filter
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.PlayerName.toLowerCase().includes(searchTerm) ||
        t.PlayerEmail.toLowerCase().includes(searchTerm) ||
        t.ErrorDescription?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply error type filter
    const errorType = this.errorTypeControl.value;
    if (errorType !== null) {
      filtered = filtered.filter(t => t.ErrorType === errorType);
    }

    // Organize into columns
    this.organizeTickets(filtered);
  }

  private organizeTickets(tickets: ISupportRequest[]): void {
    const currentUserId = this.activeUserService.activeUser().Id;

    // Temporary arrays for organizing
    const unassigned: ISupportRequest[] = [];
    const assigned: ISupportRequest[] = [];
    const myWork: ISupportRequest[] = [];
    const escalated: ISupportRequest[] = [];
    const resolved: ISupportRequest[] = [];

    for (const ticket of tickets) {
      if (ticket.Resolved) {
        resolved.push(ticket);
      } else if (ticket.Escalated) {
        escalated.push(ticket);
      } else if (ticket.Assignee === currentUserId) {
        myWork.push(ticket);
      } else if (ticket.Assignee) {
        assigned.push(ticket);
      } else {
        unassigned.push(ticket);
      }
    }

    // Sort by created date (newest first for most columns, oldest first for unassigned to prioritize)
    unassigned.sort((a, b) => new Date(a.CreatedOn).getTime() - new Date(b.CreatedOn).getTime());
    assigned.sort((a, b) => new Date(b.CreatedOn).getTime() - new Date(a.CreatedOn).getTime());
    myWork.sort((a, b) => new Date(a.CreatedOn).getTime() - new Date(b.CreatedOn).getTime());
    escalated.sort((a, b) => new Date(a.CreatedOn).getTime() - new Date(b.CreatedOn).getTime());
    resolved.sort((a, b) => new Date(b.CreatedOn).getTime() - new Date(a.CreatedOn).getTime());

    // Update signals
    this.unassignedTickets.set(unassigned);
    this.assignedTickets.set(assigned);
    this.myWorkTickets.set(myWork);
    this.escalatedTickets.set(escalated);
    this.resolvedTickets.set(resolved.slice(0, 20)); // Limit resolved to last 20
  }

  onTicketClicked(ticket: ISupportRequest): void {
    this.selectedTicket = ticket;
    this.isPanelOpen = true;
  }

  onPanelClose(): void {
    this.isPanelOpen = false;
    this.selectedTicket = null;
  }

  onTicketUpdated(updatedTicket: ISupportRequest): void {
    // Update ticket in allTickets array
    const index = this.allTickets.findIndex(t => t.Id === updatedTicket.Id);
    if (index !== -1) {
      this.allTickets[index] = updatedTicket;
    }
    this.selectedTicket = updatedTicket;
    this.filterAndOrganizeTickets();
  }

  onQuickAssignToMe(ticket: ISupportRequest): void {
    const command: IAssignAdminToSupportRequest = {
      AdminId: this.activeUserService.activeUser().Id,
      RequestId: ticket.Id,
      AssigneeId: this.activeUserService.activeUser().Id
    };

    if (!this.isCrossGameView) {
      command.GameId = this.gameService.activeGame().Id;
    }

    this.supportRequestService.assignAdminToSupportRequest(command).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Ticket assigned to you');
        // Update ticket locally
        const index = this.allTickets.findIndex(t => t.Id === ticket.Id);
        if (index !== -1) {
          this.allTickets[index] = {
            ...this.allTickets[index],
            Assignee: this.activeUserService.activeUser().Id
          };
        }
        this.filterAndOrganizeTickets();
      },
      error: () => {
        this.snackBarService.openErrorSnackBar('Failed to assign ticket');
      }
    });
  }

  refreshBoard(): void {
    this.loadTickets();
  }

  clearFilters(): void {
    this.searchControl.setValue('');
    this.errorTypeControl.setValue(null);
  }

  toggleView(): void {
    this.isVerticalView.update(v => !v);
  }

  toggleColumnVisibility(column: 'unassigned' | 'assigned' | 'myWork' | 'escalated' | 'resolved'): void {
    this.columnVisibility.update(cv => ({
      ...cv,
      [column]: !cv[column]
    }));
  }

  onViewPlayerTickets(playerEmail: string): void {
    this.searchControl.setValue(playerEmail);
  }
}
